/**
 * Wrapper reutilizável que junta `useDevicePermissionRequest` (orquestração) com o
 * `PermissionPrimingSheet` (UI) num único ponto de consumo.
 *
 * Cada entrada de recurso (câmera/microfone) faz:
 *   const { requestDeviceAccess, permissionSheet } = useDevicePermissionGate()
 *   ...
 *   const outcome = await requestDeviceAccess('microphone', { reason: '…' })
 *   if (outcome !== 'granted') return  // não segue com a captura
 *   ...
 *   return (<>{seuJSX}{permissionSheet}</>)
 *
 * Regras herdadas do hook: contexto já concedido NÃO reabre o sheet; primeiro uso
 * mostra o racional antes do prompt do SO; negado → orientação de Configurações.
 */

import type { ReactElement } from 'react'
import { PermissionPrimingSheet } from '@/shared/ui/PermissionPrimingSheet'
import {
  useDevicePermissionRequest,
  type DeviceAccessOutcome,
  type DeviceKind,
  type RequestDeviceAccessOptions,
} from './useDevicePermissionRequest'

export interface UseDevicePermissionGateResult {
  /** Solicita acesso; orquestra sheet + prompt e resolve o desfecho. */
  requestDeviceAccess: (
    kind: DeviceKind,
    opts: RequestDeviceAccessOptions,
  ) => Promise<DeviceAccessOutcome>
  /** Elemento pronto para renderizar; hospeda o sheet de priming/bloqueio (null quando fechado). */
  permissionSheet: ReactElement | null
}

export function useDevicePermissionGate(): UseDevicePermissionGateResult {
  const { requestDeviceAccess, sheet, confirm, dismiss, openSettings } =
    useDevicePermissionRequest()

  const permissionSheet = sheet ? (
    <PermissionPrimingSheet
      open={sheet.open}
      kind={sheet.kind}
      reason={sheet.reason}
      mode={sheet.mode}
      busy={sheet.busy}
      onConfirm={() => void confirm()}
      onDismiss={dismiss}
      onOpenSettings={() => void openSettings()}
    />
  ) : null

  return { requestDeviceAccess, permissionSheet }
}
